import { Input } from '@/components/shadcn-ui/input';
import { ChevronRight, Search } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLotStore } from '@/stores/useLot.store';
import { Link } from 'react-router-dom';
import { Card } from '@/components/shadcn-ui/card';
import { useDebounceValue } from '@/hooks/useDebouncedValue';
import Loader from '@/components/sharedComponents/Loader';
import { getLotStageLabel } from '@/lib/get-lot-stage-label';

const LIMIT = 20;

const LotsPage = () => {
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState('');

  const fetchLots = useLotStore(state => state.fetchPaginatedLots);
  const lots = useLotStore(state => state.lots);
  const pagination = useLotStore(state => state.pagination);

  // Refs to avoid re-creating observer
  const observerRef = useRef(null);
  const loadingRef = useRef(false);
  const paginationRef = useRef(pagination);
  const fetchLotsRef = useRef(fetchLots);
  const queryRef = useRef('');

  /* ---------------- keep refs in sync ---------------- */
  useEffect(() => {
    paginationRef.current = pagination;
  }, [pagination]);

  useEffect(() => {
    fetchLotsRef.current = fetchLots;
  }, [fetchLots]);

  /* ---------------- initial load ---------------- */
  // useEffect(() => {
  //   fetchLots();
  // }, [fetchLots]);

  /* ---------------- search + page 1 fetch ---------------- */
  const debouncedQuery = useDebounceValue(query, 300);

  useEffect(() => {
    queryRef.current = debouncedQuery || '';
    setPage(1);

    loadingRef.current = true;
    setLoading(true);

    fetchLotsRef.current(queryRef.current, 1, LIMIT).finally(() => {
      loadingRef.current = false;
      setLoading(false);
    });
  }, [debouncedQuery]);

  /* ---------------- next page fetch ---------------- */
  useEffect(() => {
    if (page === 1) return;
    if (loadingRef.current) return;

    loadingRef.current = true;
    setLoading(true);

    fetchLotsRef.current(queryRef.current, page, LIMIT).finally(() => {
      loadingRef.current = false;
      setLoading(false);
    });
  }, [page]);

  /* ---------------- intersection observer ---------------- */
  const observerCallbackRef = useCallback(node => {
    if (observerRef.current) observerRef.current.disconnect();
    if (!node) return;

    observerRef.current = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && !loadingRef.current && paginationRef.current?.hasNextPage) {
          setPage(prev => prev + 1);
        }
      },
      { rootMargin: '150px' }
    );

    observerRef.current.observe(node);
  }, []);

  return (
    <div className="flex flex-1 flex-col overflow-hidden px-4 py-[18px]">
      {/* Search (fixed) */}
      <div className="relative mb-[18px] h-11">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search lots..."
          className="h-full rounded-[8px] bg-white pl-9 text-sm"
          value={query}
          onChange={e => setQuery(e.target.value)}
        />
      </div>

      {/* Scrollable List ONLY */}
      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
        {lots?.map(lot => (
          <Link key={lot._id} to={`lot-detail/${lot._id}`} className="text-left">
            <Card className="flex items-center justify-between rounded-2xl border border-zinc-200 bg-white px-4 py-3 shadow-sm">
              <div className="flex-1 space-y-0.5 overflow-hidden">
                <p className="mb-1 text-base font-medium uppercase">{lot?.lotname}</p>
                <p className="text-sm text-muted-foreground">{lot?.estate?.name}</p>
                <p className="text-sm text-muted-foreground">{getLotStageLabel(lot?.stage)}</p>
              </div>
              <ChevronRight className="h-4 w-4 min-w-5 text-muted-foreground" />
            </Card>
          </Link>
        ))}

        {pagination?.hasNextPage && <div ref={observerCallbackRef} className="h-4" />}

        {loading && (
          <div className="flex justify-center py-4">
            <Loader />
          </div>
        )}

        {lots?.length === 0 && <p className="py-4 text-center text-xs text-muted-foreground">No lots found.</p>}
      </div>
    </div>
  );
};

export default LotsPage;
